// ParrotCart.tsx
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePlayer } from '@/hooks/usePlayer'

const BG = '#07000a'
const PINK = '#ff2680'
const GOLD = '#ffd700'
const MONO = "'JetBrains Mono', 'Courier New', monospace"

const scanlineStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 100,
  backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.07) 2px, rgba(0,0,0,0.07) 4px)',
}

const W = 480
const H = 640
const LANES = 5
const LANE_W = W / LANES
const CART_W = 44
const CART_H = 56
const RIVAL_W = 44
const RIVAL_H = 56

interface Rival {
  id: number
  lane: number
  y: number
  color: string
  emoji: string
}

interface Particle {
  x: number; y: number; vx: number; vy: number; life: number; color: string
}

interface GameState {
  lane: number
  y: number
  rivals: Rival[]
  score: number
  speed: number
  lives: number
  gameOver: boolean
  dist: number
  particles: Particle[]
  changeCooldown: number
}

const RIVAL_COLORS = [PINK, '#00cfff', '#00ff88', '#ff9900', '#cc44ff']
const RIVAL_EMOJIS = ['🦜', '🐦', '🦚', '🦩', '🕊️']
let nextId = 0

function laneX(lane: number) { return lane * LANE_W + LANE_W / 2 - CART_W / 2 }

function initState(): GameState {
  nextId = 0
  return {
    lane: 2,
    y: H - CART_H - 30,
    rivals: [],
    score: 0,
    speed: 4,
    lives: 3,
    gameOver: false,
    dist: 0,
    particles: [],
    changeCooldown: 0,
  }
}

export default function ParrotCart() {
  const navigate = useNavigate()
  const { submitScore } = usePlayer()  // ADD
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const stateRef = useRef<GameState>(initState())
  const keysRef = useRef<Record<string, boolean>>({})
  const rafRef = useRef<number>(0)
  const [displayScore, setDisplayScore] = useState(0)
  const [displayLives, setDisplayLives] = useState(3)
  const [gameOver, setGameOver] = useState(false)
  const [finalScore, setFinalScore] = useState(0)
  const frameRef = useRef(0)

  const restart = useCallback(() => {
    stateRef.current = initState()
    setDisplayScore(0)
    setDisplayLives(3)
    setGameOver(false)
    setFinalScore(0)
    frameRef.current = 0
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      keysRef.current[e.code] = true
      if (['ArrowLeft', 'ArrowRight', 'Space'].includes(e.code)) e.preventDefault()
    }
    const onKeyUp = (e: KeyboardEvent) => { keysRef.current[e.code] = false }
    window.addEventListener('keydown', onKey)
    window.addEventListener('keyup', onKeyUp)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKeyUp) }
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!

    function drawCart(x: number, y: number, color: string, isPlayer: boolean) {
      ctx.fillStyle = color
      ctx.shadowColor = color
      ctx.shadowBlur = isPlayer ? 16 : 6
      ctx.fillRect(x + 4, y + 8, CART_W - 8, CART_H - 20)
      ctx.shadowBlur = 0
      ctx.fillStyle = isPlayer ? '#fff' : 'rgba(255,255,255,0.5)'
      ctx.fillRect(x + 10, y + 12, CART_W - 20, 14)
      ctx.fillStyle = '#222'
      ctx.fillRect(x, y + CART_H - 18, 10, 12)
      ctx.fillRect(x + CART_W - 10, y + CART_H - 18, 10, 12)
      ctx.fillRect(x + 2, y + 6, 8, 10)
      ctx.fillRect(x + CART_W - 10, y + 6, 8, 10)
      ctx.fillStyle = GOLD
      ctx.fillRect(x + 2, y + CART_H - 16, 6, 8)
      ctx.fillRect(x + CART_W - 8, y + CART_H - 16, 6, 8)
      if (isPlayer) {
        ctx.font = '18px serif'
        ctx.fillText('🦜', x + CART_W / 2 - 9, y + 8)
      }
    }

    function spawnRival(s: GameState) {
      const lane = Math.floor(Math.random() * LANES)
      const tooClose = s.rivals.some(r => r.lane === lane && r.y < 120)
      if (tooClose) return
      const idx = Math.floor(Math.random() * RIVAL_COLORS.length)
      s.rivals.push({ id: nextId++, lane, y: -RIVAL_H - 10, color: RIVAL_COLORS[idx], emoji: RIVAL_EMOJIS[idx] })
    }

    function loop() {
      const s = stateRef.current
      if (s.gameOver) return
      frameRef.current++
      const keys = keysRef.current

      if (s.changeCooldown > 0) s.changeCooldown--
      if (keys['ArrowLeft'] && s.lane > 0 && s.changeCooldown === 0) {
        s.lane--
        s.changeCooldown = 14
        for (let i = 0; i < 6; i++) {
          s.particles.push({ x: laneX(s.lane + 1) + CART_W / 2, y: s.y + CART_H, vx: -2 - Math.random() * 2, vy: -1 + Math.random() * 2, life: 20, color: GOLD })
        }
      }
      if (keys['ArrowRight'] && s.lane < LANES - 1 && s.changeCooldown === 0) {
        s.lane++
        s.changeCooldown = 14
        for (let i = 0; i < 6; i++) {
          s.particles.push({ x: laneX(s.lane - 1) + CART_W / 2, y: s.y + CART_H, vx: 2 + Math.random() * 2, vy: -1 + Math.random() * 2, life: 20, color: GOLD })
        }
      }

      const targetX = laneX(s.lane)
      s.speed = 4 + s.dist / 800
      s.dist += s.speed
      s.score = Math.floor(s.dist / 10)

      const spawnRate = Math.max(40, 80 - Math.floor(s.dist / 200))
      if (frameRef.current % spawnRate === 0) spawnRival(s)

      for (const r of s.rivals) { r.y += s.speed * 0.9 }

      const px = laneX(s.lane)
      for (const r of s.rivals) {
        const rx = laneX(r.lane)
        if (
          r.y + RIVAL_H > s.y + 6 &&
          r.y < s.y + CART_H - 6 &&
          rx < px + CART_W - 6 &&
          rx + RIVAL_W > px + 6
        ) {
          s.lives--
          for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2
            s.particles.push({ x: px + CART_W / 2, y: s.y + CART_H / 2, vx: Math.cos(angle) * 4, vy: Math.sin(angle) * 4, life: 35, color: PINK })
          }
          r.y = H + 200
          if (s.lives <= 0) {
            s.gameOver = true
            setFinalScore(s.score)
            setGameOver(true)
            submitScore('parrot-cart', s.score)  // ADD
            return
          }
          setDisplayLives(s.lives)
        }
      }

      s.rivals = s.rivals.filter(r => r.y < H + 80)

      for (const p of s.particles) { p.x += p.vx; p.y += p.vy; p.life-- }
      s.particles = s.particles.filter(p => p.life > 0)

      ctx.fillStyle = BG
      ctx.fillRect(0, 0, W, H)

      for (let i = 0; i < LANES; i++) {
        const lx = i * LANE_W
        ctx.fillStyle = i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.0)'
        ctx.fillRect(lx, 0, LANE_W, H)
      }

      const dashOffset = (frameRef.current * s.speed) % 60
      ctx.setLineDash([30, 30])
      ctx.lineDashOffset = -dashOffset
      ctx.strokeStyle = 'rgba(255,214,0,0.15)'
      ctx.lineWidth = 2
      for (let i = 1; i < LANES; i++) {
        ctx.beginPath()
        ctx.moveTo(i * LANE_W, 0)
        ctx.lineTo(i * LANE_W, H)
        ctx.stroke()
      }
      ctx.setLineDash([])

      ctx.fillStyle = 'rgba(255,38,128,0.1)'
      ctx.fillRect(0, 0, 6, H)
      ctx.fillRect(W - 6, 0, 6, H)
      ctx.fillStyle = PINK
      ctx.fillRect(0, 0, 2, H)
      ctx.fillRect(W - 2, 0, 2, H)

      for (const r of s.rivals) {
        drawCart(laneX(r.lane), r.y, r.color, false)
        ctx.font = '14px serif'
        ctx.fillText(r.emoji, laneX(r.lane) + CART_W / 2 - 7, r.y + 8)
      }

      for (const p of s.particles) {
        ctx.globalAlpha = p.life / 35
        ctx.fillStyle = p.color
        ctx.fillRect(p.x - 3, p.y - 3, 6, 6)
      }
      ctx.globalAlpha = 1

      drawCart(targetX, s.y, GOLD, true)

      const speedPct = Math.min((s.speed - 4) / 8, 1)
      ctx.fillStyle = `rgba(255,215,0,${0.2 + speedPct * 0.5})`
      ctx.font = `bold 10px ${MONO}`
      ctx.fillText(`${s.speed.toFixed(1)}x`, W - 50, H - 14)

      setDisplayScore(s.score)
      rafRef.current = requestAnimationFrame(loop)
    }

    rafRef.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(rafRef.current)
  }, [gameOver, submitScore])  // ADD submitScore to dep array

  return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, overflowX: 'hidden' }}>
      <div style={scanlineStyle} />
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        {Array.from({ length: LANES + 1 }).map((_, i) => (
          <div key={i} style={{ position: 'absolute', top: 0, bottom: 0, left: `${(i / (LANES + 1)) * 100}%`, width: 1, background: 'rgba(255,215,0,0.04)' }} />
        ))}
      </div>

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <button onClick={() => navigate('/games')} style={{ alignSelf: 'flex-start', marginBottom: 12, background: 'none', border: `1px solid rgba(255,38,128,0.3)`, color: 'rgba(255,38,128,0.6)', fontFamily: MONO, fontSize: 10, letterSpacing: 2, padding: '5px 12px', cursor: 'pointer', borderRadius: 4 }}>
          ← BACK
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: W, marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: GOLD, letterSpacing: 2 }}>PARROT CART</div>
          <div style={{ display: 'flex', gap: 16 }}>
            <span style={{ fontSize: 11, color: GOLD, letterSpacing: 2 }}>SCORE: {displayScore}</span>
            <span style={{ fontSize: 11, color: PINK, letterSpacing: 2 }}>{'❤️'.repeat(displayLives)}</span>
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <canvas ref={canvasRef} width={W} height={H} style={{ display: 'block', border: `1px solid rgba(255,215,0,0.15)`, boxShadow: `0 0 40px rgba(255,215,0,0.08)` }} />

          {gameOver && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(7,0,10,0.88)', backdropFilter: 'blur(2px)' }}>
              <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: 6, color: GOLD, fontFamily: "'Arial Black', sans-serif", marginBottom: 8 }}>CRASH!</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', letterSpacing: 3, marginBottom: 4 }}>GAME OVER</div>
              <div style={{ fontSize: 22, color: GOLD, fontWeight: 700, letterSpacing: 3, marginBottom: 28 }}>{finalScore} PTS</div>
              <button onClick={restart} style={{ background: 'none', border: `1px solid ${GOLD}`, color: GOLD, fontFamily: MONO, fontSize: 11, letterSpacing: 3, padding: '10px 28px', cursor: 'pointer', borderRadius: 4 }}>
                RETRY
              </button>
            </div>
          )}
        </div>

        <div style={{ marginTop: 10, fontSize: 9, color: 'rgba(255,255,255,0.2)', letterSpacing: 2 }}>
          ← → SWITCH LANES &nbsp;|&nbsp; AVOID RIVALS
        </div>
      </div>
    </div>
  )
}